import React, { useState, useEffect } from 'react';
import { LayoutDashboard } from 'lucide-react';
import { useUser } from '@/store/useUser';

export const DashboardSettings = () => {
  const { user, updateDashboardPreference } = useUser();
  const [dashboardPreference, setDashboardPreference] = useState<'legacy' | 'DD2'>('legacy');
  const [isSavingDashboard, setIsSavingDashboard] = useState(false);
  const [dashboardError, setDashboardError] = useState('');

  useEffect(() => {
    if (user?.settings?.dashboard === 'DD2') {
      setDashboardPreference('DD2');
      return;
    }
    setDashboardPreference('legacy');
  }, [user]);

  const handleDashboardChange = async (value: 'legacy' | 'DD2') => {
    if (value !== 'legacy' && value !== 'DD2') {
      return;
    }
    setDashboardPreference(value);
    setDashboardError('');
    if (!updateDashboardPreference) {
      return;
    }
    setIsSavingDashboard(true);
    const result = await updateDashboardPreference(value);
    setIsSavingDashboard(false);
    if (result?.success) {
      return;
    }
    setDashboardError(result?.error || 'Failed to update dashboard preference');
  };

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="bg-white dark:bg-neutral-800 rounded-xl p-6 shadow-sm border border-gray-100 dark:border-neutral-700">
        <div className="flex items-center gap-3 mb-6">
             <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <LayoutDashboard className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Dashboard Preference</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                    Choose which dashboard opens after you sign in
                </p>
            </div>
        </div>
        
        <div className="space-y-3">
          {[
            {
              id: 'legacy',
              name: 'Legacy Dashboard',
              description: 'The classic interface for daily operations.',
              path: '/menu.php'
            },
            {
              id: 'DD2',
              name: 'Modern Dashboard',
              description: 'A data-driven, interactive view of your projects.',
              path: '/dashboard'
            }
          ].map((option) => {
            const isSelected = dashboardPreference === option.id;
            return (
              <button
                key={option.id}
                onClick={() => handleDashboardChange(option.id as 'legacy' | 'DD2')}
                aria-pressed={isSelected}
                data-testid={`settings-dashboard-${option.id}`}
                className={`w-full flex items-center p-4 border rounded-xl transition-all duration-200 ${
                  isSelected 
                    ? 'ring-2 ring-blue-500 border-transparent bg-blue-50 dark:bg-blue-900/20' 
                    : 'border-gray-200 dark:border-neutral-700 hover:border-blue-300 dark:hover:border-blue-700 hover:bg-gray-50 dark:hover:bg-neutral-800'
                }`}
              >
                <div className={`flex-shrink-0 w-5 h-5 rounded-full border-2 mr-4 flex items-center justify-center transition-colors ${
                  isSelected ? 'border-blue-600 bg-blue-600' : 'border-gray-300 dark:border-neutral-600'
                }`}>
                  {isSelected && <div className="w-2 h-2 bg-white rounded-full" />}
                </div>
                
                <div className="flex-1 text-left">
                  <div className="flex items-center justify-between">
                    <span className={`font-semibold ${isSelected ? 'text-blue-900 dark:text-blue-100' : 'text-gray-900 dark:text-gray-100'}`}>
                      {option.name}
                    </span>
                    <span className="text-xs font-mono text-gray-400 bg-gray-100 dark:bg-neutral-900 px-2 py-0.5 rounded">
                      {option.path}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {option.description}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
        
        {isSavingDashboard && (
          <div className="flex items-center gap-2 mt-4 text-sm text-blue-600 dark:text-blue-400 animate-pulse">
            <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" />
            Saving preference...
          </div>
        )}
        
        {dashboardError && (
          <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-600 dark:text-red-400">
            {dashboardError}
          </div>
        )}
      </div>
    </div>
  );
};
